<script setup>
import { ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import MainSection from "@/components/MainSection.vue";
import CardComponent from "@/components/CardComponent.vue";
import TitleBar from "@/components/TitleBar.vue";
import JbButton from "@/components/JbButton.vue";
import Icon from "@/components/Icon.vue";
import ModalBox from "@/components/ModalBox.vue";
import {
    mdiAlertCircleOutline,
    mdiRefresh,
    mdiCreditCardOutline,
    mdiWalletOutline,
    mdiCalendarBlankOutline,
    mdiOpenInNew,
} from "@mdi/js";
import { openBillingPortal, getStripeErrorMessage } from "@/services/stripe.service";
import { useToast } from '@/composables/useToast';
import SubscriptionPlans from "@/views/SubscriptionPlans.vue";
import DiscountBanner from "@/components/DiscountBanner.vue";
import PaymentCancelledNotice from "@/components/PaymentCancelledNotice.vue";
import { trackGoal } from "@/services/datafast-goal.service";
import { takeCancelledNotice } from "@/store/products.module";

// "Billing", the sidebar group this lives under — it has no page of its own,
// so the crumb carries no link.
const titleStack = ref(["Billing", "Subscriptions"]);

/* Back from a subscription checkout started on this page and left unpaid
   (Stripe's cancel_url, via the `/cancel` route). One-shot, and plans only: a
   cancelled package is left for the payment step, which can put it back. */
const cancelledNotice = takeCancelledNotice((kind) => kind === 'plan');
const store = useStore();
const { notifyError } = useToast();

const subscriptions = computed(() => store.state.subscriptions.subscriptions);
const loading = computed(() => store.state.subscriptions.loading);

/*
 * Gated on a *successful* lookup, not on `loading` alone: a failed request also
 * clears `loading`, and an empty list is indistinguishable from "we never got
 * an answer". Reuses the store state this page already loads — no second fetch.
 */
const subscriptionsLoaded = ref(false);

/*
 * `createPortalSession` looks for a subscription with status exactly 'active'
 * and 404s otherwise, so `trialing` / `past_due` / `canceled` must not be
 * offered the portal — for them it genuinely does not open. Same predicate as
 * Invoices.vue and Products.vue.
 */
const hasActiveSubscription = computed(() =>
    (subscriptions.value ?? []).some((sub) => sub.status === 'active')
);

const canManageBilling = computed(() => subscriptionsLoaded.value && hasActiveSubscription.value);

const confirmPortalActive = ref(false);

// The portal session is created server-side before the redirect, so the button
// has to stay busy — and un-clickable — until the navigation happens.
const portalPending = ref(false);

// `subscriptions/getSubscriptions` has no error branch of its own; without this
// a failed request just leaves an empty list and no explanation.
const loadError = ref("");

const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

// Shared `.badge-*` pill per Stripe status — same buckets the old text colours used.
const statusMeta = (status) => {
    switch (status) {
        case 'active':
            return { label: 'Active', class: 'badge-success' };
        case 'trialing':
            return { label: 'Trialing', class: 'badge-warning' };
        case 'canceled':
        case 'incomplete':
            return {
                label: status.charAt(0).toUpperCase() + status.slice(1),
                class: 'badge-danger',
            };
        default:
            return {
                label: String(status ?? '').charAt(0).toUpperCase() + String(status ?? '').slice(1),
                class: 'badge bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
            };
    }
};

const manageSubscription = async () => {
    if (portalPending.value) return;
    portalPending.value = true;
    const currentSubscriptions = subscriptions.value ?? [];
    trackGoal("open_billing_portal", {
        subscriptions_count: currentSubscriptions.length,
        has_active_subscription: currentSubscriptions.some((sub) => sub.status === 'active') ? 'true' : 'false'
    });
    /* The portal session is created by an authenticated request that answers
       `{ url }`; the service navigates once it has one. There is no user id to
       send — the backend takes it from the token. */
    try {
        await openBillingPortal();
    } catch (error) {
        trackGoal("open_billing_portal_failed", {
            error_message: error?.message ?? 'unknown'
        });

        // The old fallback sent the user to Stripe's generic portal login, which
        // only emails them a link — it hid the real failure (usually "no active
        // subscription") behind a dead end. Say what happened instead.
        portalPending.value = false;
        // 401 here means the session is genuinely gone (the interceptor already
        // retried), so the only useful button is a sign-in — anything else just
        // fails again. Every other status gets a plain Retry.
        const expired = error?.response?.status === 401;
        notifyError("We couldn't open the billing portal", {
            detail: getStripeErrorMessage(error, "Nothing was charged or changed."),
            dedupeKey: "billing-portal-failed",
            action: expired
                ? { label: "Sign in again", onClick: () => window.location.assign("/login"), keepOpen: true }
                : { label: "Retry", onClick: () => manageSubscription() }
        });
    }
};

const refreshSubscriptions = () => {
    loadError.value = "";
    Promise.resolve(store.dispatch("subscriptions/getSubscriptions"))
        .then(() => {
            subscriptionsLoaded.value = true;
        })
        .catch(() => {
            subscriptionsLoaded.value = false;
            loadError.value = "We couldn't load your subscriptions. Check your connection and try again.";
        });
};

onMounted(() => {
    refreshSubscriptions();
    trackGoal("open_billing_overview");
});
</script>

<template>
    <!-- Leaving the app for Stripe is where cancelling happens, so confirm first. -->
    <teleport to="body">
        <modal-box v-model="confirmPortalActive" title="Open the Stripe billing portal?" :show-buttons="false"
            :dismissable="!portalPending">
            <div class="space-y-4">
                <p class="text-sm text-gray-700 dark:text-gray-300">
                    You'll be taken to Stripe, where you can update your payment method, change your plan or cancel
                    your subscription. Cancelling stops future token allocations.
                </p>
                <div class="flex flex-col sm:flex-row gap-3">
                    <jb-button :icon="mdiOpenInNew"
                        :label="portalPending ? 'Opening Stripe…' : 'Continue to Stripe'" color="info" class="flex-1"
                        :spinner="portalPending" :disabled="portalPending" @click="manageSubscription" />
                    <jb-button label="Stay here" color="white" class="flex-1" :disabled="portalPending"
                        @click="confirmPortalActive = false" />
                </div>
                <p v-if="portalPending" class="text-center text-xs text-gray-500 dark:text-gray-400" role="status"
                    aria-live="polite">
                    Taking you to Stripe — please don't close this tab.
                </p>
            </div>
        </modal-box>
    </teleport>

    <main-section>
        <discount-banner />

        <title-bar :title-stack="titleStack"
            subtitle="Your recurring token plans and where to manage the billing details.">
            <template #actions>
                <jb-button :icon="mdiRefresh" color="white" @click="refreshSubscriptions" :disabled="loading"
                    popper-content="Refresh subscriptions" aria-label="Refresh subscriptions" />
                <!-- Absent unless there is an active plan: the portal 404s for
                     everyone else, so the button could only ever fail. -->
                <jb-button v-if="canManageBilling" :icon="mdiCreditCardOutline" label="Manage in Stripe" color="info"
                    @click="confirmPortalActive = true" :disabled="loading" />
            </template>
        </title-bar>

        <payment-cancelled-notice v-if="cancelledNotice">
            You left Stripe before subscribing. Pick a plan below to try again.
        </payment-cancelled-notice>

        <div class="space-y-8">
            <!-- ─── Current subscriptions ─── -->
            <card-component title="Your subscriptions" :icon="mdiWalletOutline">
                <!-- Loading skeleton -->
                <div v-if="loading" class="space-y-3">
                    <div v-for="n in 2" :key="`sub-skeleton-${n}`"
                        class="rounded-xl bg-gray-50 dark:bg-gray-900 p-4 space-y-3">
                        <div class="flex items-center gap-3">
                            <div class="h-5 w-20 rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
                            <div class="h-5 w-32 rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
                        </div>
                        <div class="h-4 w-2/3 rounded-lg animate-pulse bg-gray-200 dark:bg-gray-700" />
                    </div>
                </div>

                <!-- Error -->
                <div v-else-if="loadError" class="flex flex-col gap-3 py-2 sm:flex-row sm:items-start" role="alert">
                    <span
                        class="inline-flex items-center justify-center w-11 h-11 flex-shrink-0 rounded-xl bg-red-100 text-red-600 dark:bg-red-900 dark:bg-opacity-40 dark:text-red-400">
                        <icon :path="mdiAlertCircleOutline" :size="20" w="w-5" h="h-5" />
                    </span>
                    <div class="min-w-0 flex-1">
                        <p class="text-base font-semibold text-gray-900 dark:text-gray-100">Couldn't load your
                            subscriptions</p>
                        <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">{{ loadError }}</p>
                        <jb-button class="mt-4" color="info" :icon="mdiRefresh" label="Try again"
                            @click="refreshSubscriptions" />
                    </div>
                </div>

                <!-- Empty -->
                <div v-else-if="!subscriptions || subscriptions.length === 0"
                    class="flex flex-col gap-3 py-2 sm:flex-row sm:items-start">
                    <span
                        class="inline-flex items-center justify-center w-11 h-11 flex-shrink-0 rounded-xl bg-brand-100 text-brand-600 dark:bg-brand-900 dark:bg-opacity-40 dark:text-brand-400">
                        <icon :path="mdiWalletOutline" :size="20" w="w-5" h="h-5" />
                    </span>
                    <div class="min-w-0 flex-1">
                        <p class="text-base font-semibold text-gray-900 dark:text-gray-100">No active subscriptions</p>
                        <p class="mt-0.5 text-sm text-gray-600 dark:text-gray-300">
                            You're on pay-as-you-go. Pick a plan below to top your balance up every month.
                        </p>
                        <jb-button class="mt-4" color="info" label="See the plans" href="#subscription-plans" />
                    </div>
                </div>

                <!-- List — card per subscription so it never blows out on mobile -->
                <ul v-else class="space-y-3">
                    <li v-for="sub in subscriptions" :key="sub.id" class="rounded-xl bg-gray-50 dark:bg-gray-900 p-4">
                        <div class="flex flex-wrap items-center justify-between gap-3">
                            <div class="min-w-0">
                                <div class="flex items-center gap-2 flex-wrap">
                                    <span class="text-xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                        <span class="notranslate tabular-nums" translate="no">€{{ sub.plan_amount
                                            }}</span>
                                        <span class="text-sm font-medium text-gray-500 dark:text-gray-400">/month</span>
                                    </span>
                                    <span :class="statusMeta(sub.status).class">
                                        {{ statusMeta(sub.status).label }}
                                    </span>
                                </div>
                                <p class="notranslate mt-1 text-xs text-gray-500 dark:text-gray-400 truncate font-mono"
                                    translate="no" :title="sub.stripe_subscription_id">
                                    {{ sub.stripe_subscription_id }}
                                </p>
                            </div>

                            <!-- Per row, not per page: a canceled or past_due row
                                 offering "Manage" either 404s outright or opens
                                 the portal for a *different* subscription. Only
                                 an active row has a portal of its own. -->
                            <jb-button v-if="sub.status === 'active'" color="white" small
                                :icon="mdiCreditCardOutline" label="Manage" @click="confirmPortalActive = true" />
                        </div>

                        <div
                            class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 border-t border-gray-200 dark:border-gray-700 pt-3">
                            <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <icon :path="mdiCalendarBlankOutline" :size="16" w="w-4" h="h-4"
                                    class="flex-shrink-0 text-gray-400 dark:text-gray-500" />
                                <span>Renews {{ formatDate(sub.current_period_end) }}</span>
                            </div>
                            <div class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                <icon :path="mdiCalendarBlankOutline" :size="16" w="w-4" h="h-4"
                                    class="flex-shrink-0 text-gray-400 dark:text-gray-500" />
                                <span>Started {{ formatDate(sub.created_at) }}</span>
                            </div>
                        </div>
                    </li>
                </ul>
            </card-component>

            <!-- ─── Available plans ─── -->
            <div id="subscription-plans">
                <subscription-plans />
            </div>
        </div>
    </main-section>
</template>
